import { Directive, ElementRef, inject, signal } from '@angular/core';
import { FALLBACK_IMAGE } from '../core/constants';

@Directive({
  selector: 'img[appFallbackImage]',
  // The qm-fallback class marks any img currently showing the placeholder (failed load OR an
  // empty src resolved to FALLBACK_IMAGE), so a theme can recolor the light placeholder to suit
  // its surface (e.g. spotlight darkens it) without touching real photos.
  host: { '(error)': 'onError()', '(load)': 'onLoad()', '[class.qm-fallback]': 'failed()' },
})
export class FallbackImage {
  private ref = inject<ElementRef<HTMLImageElement>>(ElementRef);

  private readonly _failed = signal(false);
  /** True while the shown image is the placeholder (real src failed, or was empty → fallback). */
  readonly failed = this._failed.asReadonly();

  protected onError(): void {
    const img = this.ref.nativeElement;
    // Comparison guard, not a fired-once flag: keeps working when [src] later
    // changes to another broken URL (the data: URI reflects back unchanged).
    if (img.src !== FALLBACK_IMAGE) {
      img.src = FALLBACK_IMAGE;
    }
    this._failed.set(true);
  }

  protected onLoad(): void {
    // Mark failed iff the thing that loaded IS the placeholder — covers both a broken real src
    // (error → fallback → load) and an empty src bound straight to FALLBACK_IMAGE.
    this._failed.set(this.ref.nativeElement.src === FALLBACK_IMAGE);
  }
}
